import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import lottie from 'lottie-web';
import { primitives } from '@lexody/design-tokens';

const { violet, pink, orange, green } = primitives;

export const HERO_GRADIENT = [violet['700'], violet['600'], pink['600']];
export const SUNNY_GRADIENT = [orange['400'], orange['500']];
export const SUCCESS_GRADIENT = [green['500'], green['600']];

export function hexToRgba(hex, alpha) {
  const clean = String(hex).replace('#', '');
  const value = parseInt(clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean, 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
}

export function gradient(colors) {
  return `linear-gradient(135deg, ${colors.join(', ')})`;
}

export function entranceStyle(index = 0) {
  return { animationDelay: `${index * 70}ms` };
}

export const Spinner = ({ light = false }) => (
  <span className={`gpSpinner${light ? ' gpSpinner--light' : ''}`} role="status" aria-label="Loading" />
);

export const FunButton = ({
  text,
  emoji,
  onClick,
  loading = false,
  disabled = false,
  variant = 'primary',
  size = 'large',
  className = '',
}) => {
  const inactive = disabled || loading;

  if (variant === 'ghost') {
    return (
      <button type="button" className={`gpGhost ${className}`} onClick={onClick} disabled={inactive}>
        {!!emoji && <span className="gpGhost__emoji" aria-hidden="true">{emoji}</span>}
        <span>{text}</span>
      </button>
    );
  }

  const classes = [
    'gpButton',
    `gpButton--${variant}`,
    size === 'small' ? 'gpButton--small' : '',
    disabled ? 'gpButton--disabled' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button type="button" className={classes} onClick={onClick} disabled={inactive} aria-busy={loading}>
      {loading ? (
        <Spinner light={variant !== 'light' && variant !== 'soft'} />
      ) : (
        <span className="gpButton__row">
          {!!emoji && <span className="gpButton__emoji" aria-hidden="true">{emoji}</span>}
          <span>{text}</span>
        </span>
      )}
    </button>
  );
};

export const BobbingEmoji = ({ emoji, image, size = 64, tilt = '-8deg', delay = 0, className = '' }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!image && !imageFailed;
  return (
    <span
      className={`gpEmojiBadge${showImage ? ' gpEmojiBadge--image' : ''} ${className}`}
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.32,
        fontSize: size * 0.52,
        '--gp-tilt': tilt,
        animationDelay: `${delay}ms`,
      }}
    >
      {showImage
        ? <img className="gpEmojiBadge__img" src={image} alt="" onError={() => setImageFailed(true)} />
        : emoji}
    </span>
  );
};

export const GameHero = ({
  emoji,
  image,
  kicker,
  title,
  subtitle,
  colors = HERO_GRADIENT,
  centered = false,
  entranceIndex = 0,
  children,
}) => (
  <section
    className={`gpHero gpEnter${centered ? ' gpHero--centered' : ''}`}
    style={{
      ...entranceStyle(entranceIndex),
      background: gradient(colors),
      boxShadow: `0 14px 28px ${hexToRgba(colors[0], 0.3)}`,
    }}
  >
    <span className="gpHero__blob gpHero__blob--large" aria-hidden="true" />
    <span className="gpHero__blob gpHero__blob--small" aria-hidden="true" />
    <div className="gpHero__top">
      {!!(emoji || image) && <BobbingEmoji emoji={emoji} image={image} className="gpHero__emoji" />}
      <div className="gpHero__text">
        {!!kicker && <p className="gpHero__kicker">{kicker}</p>}
        {!!title && <h1 className="gpHero__title">{title}</h1>}
      </div>
    </div>
    {!!subtitle && <p className="gpHero__subtitle">{subtitle}</p>}
    {children}
  </section>
);

export const FunCard = ({ children, entranceIndex = 0, className = '' }) => (
  <div className={`gpCard gpEnter ${className}`} style={entranceStyle(entranceIndex)}>
    {children}
  </div>
);

export const CardLabel = ({ emoji, text, className = '' }) => (
  <div className={`gpCardLabel ${className}`}>
    {!!emoji && <span className="gpCardLabel__emoji" aria-hidden="true">{emoji}</span>}
    <h2 className="gpCardLabel__text">{text}</h2>
  </div>
);

export const Bubble = ({ emoji, text, tone = 'warning', className = '' }) => (
  <div className={`gpBubble gpBubble--${tone} ${className}`} role={tone === 'warning' ? 'alert' : 'status'}>
    {!!emoji && <span className="gpBubble__emoji" aria-hidden="true">{emoji}</span>}
    <span className="gpBubble__text">{text}</span>
  </div>
);

const TOAST_MS = 3200;
const TOAST_LEAVE_MS = 250;

// Floats over the page so feedback never pushes the board around. Give it a new
// `key` to show the same message again.
export const Toast = ({ emoji, text, tone = 'warning', duration = TOAST_MS }) => {
  const [phase, setPhase] = useState('shown');
  useEffect(() => {
    const leave = setTimeout(() => setPhase('leaving'), duration);
    const hide = setTimeout(() => setPhase('hidden'), duration + TOAST_LEAVE_MS);
    return () => {
      clearTimeout(leave);
      clearTimeout(hide);
    };
  }, [duration]);

  if (phase === 'hidden') return null;
  return ReactDOM.createPortal(
    <div className="gpToastWrap">
      <div
        className={`gpToast gpToast--${tone}${phase === 'leaving' ? ' gpToast--leaving' : ''}`}
        role={tone === 'warning' ? 'alert' : 'status'}
      >
        {!!emoji && <span className="gpToast__emoji" aria-hidden="true">{emoji}</span>}
        <span className="gpToast__text">{text}</span>
      </div>
    </div>,
    document.body,
  );
};

export const Pill = ({ emoji, text, light = false, className = '' }) => (
  <span className={`gpPill ${light ? 'gpPill--light' : 'gpPill--tinted'} ${className}`}>
    {!!emoji && <span className="gpPill__emoji" aria-hidden="true">{emoji}</span>}
    <span>{text}</span>
  </span>
);

// Portaled to <body>: the cards that open modals keep a transform from their
// entrance animation, which would otherwise trap `position: fixed` inside them.
export const GameModal = ({ title, children, onClose, className = '', hideTitle = false }) => {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && onClose) onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className="gpModal" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div className={`gpModal__card ${className}`} onClick={(event) => event.stopPropagation()}>
        {!!title && !hideTitle && <h2 className="gpModal__title">{title}</h2>}
        {children}
      </div>
    </div>,
    document.body,
  );
};

export class Lottie extends React.Component {
  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.src !== this.props.src) {
      this.destroy();
      this.load();
    }
  }

  componentWillUnmount() {
    this.destroy();
  }

  load() {
    if (!this.props.src || !this.container) return;
    this.animation = lottie.loadAnimation({
      container: this.container,
      renderer: 'svg',
      loop: this.props.loop === true,
      autoplay: this.props.autoplay !== false,
      path: this.props.src,
    });
    if (this.props.speed) this.animation.setSpeed(this.props.speed);
  }

  play() {
    if (this.animation) this.animation.goToAndPlay(0, true);
  }

  destroy() {
    if (this.animation) {
      this.animation.destroy();
      this.animation = null;
    }
  }

  render() {
    return (
      <div
        ref={(el) => { this.container = el; }}
        className={this.props.className}
        style={this.props.style}
        aria-hidden="true"
      />
    );
  }
}
